// src/components/product/BundleEditor/bundleUtils.js
// Pure helpers shared by Create/Edit product useFormData for bundle state.

export const round2 = (n) => Math.round((parseFloat(n) || 0) * 100) / 100;

export const componentKey = (productId, variationId) => `${productId}-${variationId || 'simple'}`;

const lineMax = (item, price) => round2((parseFloat(item.quantity) || 0) * (parseFloat(price?.unitValue) || 0));

const clampDiscount = (item, price, discount) => Math.min(Math.max(round2(discount), 0), lineMax(item, price));

export const addComponent = (items, component) => {
  const key = componentKey(component.productId, component.variationId);
  if (items.some(i => i.key === key)) return items;
  return [...items, {
    key,
    productId: component.productId,
    variationId: component.variationId || null,
    name: component.name,
    sku: component.sku || '',
    quantity: 1,
    shopPrices: {},
  }];
};

export const removeComponents = (items, keys) => items.filter(i => !keys.includes(i.key));

export const setComponentQty = (items, key, quantity) =>
  items.map(i => (i.key === key ? { ...i, quantity } : i));

export const setComponentPrice = (items, key, shopId, field, value) =>
  items.map(i => (i.key === key
    ? { ...i, shopPrices: { ...i.shopPrices, [shopId]: { unitValue: '', lineDiscount: 0, ...i.shopPrices[shopId], [field]: value } } }
    : i));

// Pre-fill a shop's values from the component's own shop price map, keeping any already set.
export const prefillShop = (items, shopId, priceMap = {}) =>
  items.map(i => {
    if (i.shopPrices[shopId]) return i;
    const unitValue = priceMap[i.key] ?? '';
    return { ...i, shopPrices: { ...i.shopPrices, [shopId]: { unitValue, lineDiscount: 0 } } };
  });

export const applyBulk = (items, { componentKeys, shopIds, action, value, sourceShopId, priceMaps = {} }) =>
  items.map(item => {
    if (!componentKeys.includes(item.key)) return item;
    const shopPrices = { ...item.shopPrices };
    shopIds.forEach(shopId => {
      const current = { unitValue: '', lineDiscount: 0, ...shopPrices[shopId] };
      let next = current;
      switch (action) {
        case 'setValue':
          next = { ...current, unitValue: round2(value) };
          next.lineDiscount = clampDiscount(item, next, next.lineDiscount);
          break;
        case 'setDiscountAmount':
          next = { ...current, lineDiscount: clampDiscount(item, current, value) };
          break;
        case 'setDiscountPercent':
          next = { ...current, lineDiscount: clampDiscount(item, current, lineMax(item, current) * (parseFloat(value) || 0) / 100) };
          break;
        case 'makeFree':
          next = { ...current, lineDiscount: lineMax(item, current) };
          break;
        case 'fillFromShopPrice': {
          const price = priceMaps[shopId]?.[item.key];
          if (price != null) {
            next = { ...current, unitValue: round2(price) };
            next.lineDiscount = clampDiscount(item, next, next.lineDiscount);
          }
          break;
        }
        case 'copyShop':
          if (sourceShopId && item.shopPrices[sourceShopId] && String(sourceShopId) !== String(shopId)) {
            next = { ...item.shopPrices[sourceShopId] };
          }
          break;
        default:
          break;
      }
      shopPrices[shopId] = next;
    });
    return { ...item, shopPrices };
  });

export const lineTotal = (item, shopId) => {
  const p = item.shopPrices[shopId];
  if (!p) return 0;
  return round2(lineMax(item, p) - (parseFloat(p.lineDiscount) || 0));
};

export const bundleTotals = (items, shopId, priceMap = {}) => {
  const bundlePrice = round2(items.reduce((s, i) => s + lineTotal(i, shopId), 0));
  const regularTotal = round2(items.reduce((s, i) => s + (parseFloat(i.quantity) || 0) * (parseFloat(priceMap[i.key]) || 0), 0));
  return { bundlePrice, regularTotal, saving: round2(regularTotal - bundlePrice) };
};

export const missingComponents = (items, priceMap) =>
  priceMap ? items.filter(i => !(i.key in priceMap)) : [];

export const serializeBundleItems = (items, shopIds) =>
  items.map((item, index) => ({
    productId: item.productId,
    variationId: item.variationId || null,
    quantity: parseFloat(item.quantity),
    sortOrder: index + 1,
    shopPrices: shopIds
      .filter(shopId => item.shopPrices[shopId])
      .map(shopId => ({
        shopId: parseInt(shopId),
        unitValue: round2(item.shopPrices[shopId].unitValue),
        lineDiscount: round2(item.shopPrices[shopId].lineDiscount),
      })),
  }));

// Map getProduct(id).bundleItems into editor state
export const bundleStateFromProduct = (bundleItems = []) => {
  const shopIds = new Set();
  const items = [...bundleItems]
    .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
    .map(bi => {
      const variationLabel = bi.componentVariation?.attributeDisplay || bi.componentVariation?.sku || '';
      const shopPrices = {};
      (bi.shopPrices || []).forEach(sp => {
        shopIds.add(sp.shopId);
        shopPrices[sp.shopId] = { unitValue: parseFloat(sp.unitValue), lineDiscount: parseFloat(sp.lineDiscount) || 0 };
      });
      return {
        key: componentKey(bi.componentProductId || bi.componentProduct?.id, bi.componentVariationId),
        productId: bi.componentProductId || bi.componentProduct?.id,
        variationId: bi.componentVariationId || null,
        name: variationLabel ? `${bi.componentProduct?.name} (${variationLabel})` : bi.componentProduct?.name,
        sku: bi.componentVariation?.sku || bi.componentProduct?.sku || '',
        quantity: parseFloat(bi.quantity),
        shopPrices,
      };
    });
  return { bundleItems: items, bundleShopIds: [...shopIds] };
};

// Returns an error map or {} (keys: bundleItems, bundleShops)
export const validateBundle = (items, shopIds, t) => {
  const errors = {};
  if (items.length === 0) {
    errors.bundleItems = t('products.bundleComponentsRequired');
    return errors;
  }
  if (items.some(i => !(parseFloat(i.quantity) > 0))) {
    errors.bundleItems = t('products.bundleQtyInvalid');
  }
  if (shopIds.length === 0) {
    errors.bundleShops = t('products.bundleShopRequired');
    return errors;
  }
  for (const shopId of shopIds) {
    for (const item of items) {
      const p = item.shopPrices[shopId];
      const value = parseFloat(p?.unitValue);
      if (!p || p.unitValue === '' || isNaN(value) || value < 0) {
        errors.bundleShops = t('products.bundleValueInvalid');
        return errors;
      }
      const disc = parseFloat(p.lineDiscount) || 0;
      if (disc < 0 || disc > lineMax(item, p) + 0.001) {
        errors.bundleShops = t('products.bundleDiscountInvalid');
        return errors;
      }
    }
  }
  return errors;
};

// { [productId-variationId]: price } of everything assigned to a shop
export const loadShopPriceMap = async (getShopAssignedProducts, shopId) => {
  const limit = 1000;
  const map = {};
  let page = 1;
  let total = 0;
  do {
    const res = await getShopAssignedProducts(shopId, { limit, page });
    (res.products || []).forEach(p => {
      map[componentKey(p.productId, p.variationId)] = p.price;
    });
    total = res.total || 0;
    page += 1;
  } while ((page - 1) * limit < total);
  return map;
};
